import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";
import { fetchLocationSuggestions, resolveZip, type LocationSuggestion } from "@/lib/location-autocomplete";
import { searchCitiesWithCountsFn } from "@/lib/location-counts.functions";

async function fetchCitySuggestionsWithCounts(query: string): Promise<LocationSuggestion[]> {
  // provider_city_counts is city-keyed, so a typed ZIP matched nothing here and
  // the dropdown stayed empty on /search. Resolve it to "City, ST" instead.
  if (/^\d{5}$/.test(query.trim())) return resolveZip(query.trim());
  const rows = await searchCitiesWithCountsFn({ data: { q: query } });
  return rows.map((r) => ({
    city: r.city,
    state: r.state,
    count: r.count,
    label: [r.city, r.state].filter(Boolean).join(", "),
  }));
}

/**
 * Single-input city / ZIP location autocomplete.
 * - 5-digit ZIP → resolves to "City, ST" via Zippopotam.us
 * - Otherwise queries Photon for cities/towns and offers picks like "City, ST"
 * - `withCounts`: use Heallexa's own provider-count-backed city suggestions
 *   instead of Photon (only cities that actually have providers); a typed ZIP
 *   still resolves via Zippopotam.us — for the /search filters, not the
 *   address-entry forms.
 */
export function LocationAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "City or ZIP",
  className,
  inputClassName,
  withCounts = false,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect?: (suggestion: LocationSuggestion) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  withCounts?: boolean;
}) {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handle = (val: string) => {
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = val.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const items = withCounts
          ? await fetchCitySuggestionsWithCounts(trimmed)
          : await fetchLocationSuggestions(trimmed);
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {}
      setLoading(false);
    }, 250);
  };

  return (
    <div className={`relative ${className ?? ""}`} ref={wrapRef}>
      <Input
        value={value}
        onChange={(e) => handle(e.target.value)}
        onFocus={() => suggestions.length && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        className={inputClassName ?? "border-0 shadow-none h-9 px-0 focus-visible:ring-0"}
      />
      {loading && (
        <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 min-w-full w-max max-w-[90vw] max-h-64 overflow-auto border border-border bg-popover shadow-md left-0">
          {suggestions.map((s) => (
            <li key={s.label}>
              <button
                type="button"
                onClick={() => {
                  onChange(s.label);
                  onSelect?.(s);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="truncate">{s.label}</span>
                </span>
                {typeof s.count === "number" && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {s.count.toLocaleString()} providers
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
